/**
 * **What it takes to sink her, and what she does on the way down.**
 *
 * `ship-sim.md` §5 used to open *"She cannot be sunk"* and name the two zeros
 * that made it true. This file is those two zeros replaced, plus the one thing
 * replacing them made necessary: a ship this port can destroy has to *become*
 * something when it is, and what she becomes is a hull that has stopped
 * steaming her circuit and is going down on her last course.
 *
 * ## Nothing new is stored, anywhere
 *
 * That is the load-bearing property of this module and it is worth stating
 * before any of the numbers. **No field is added to `EncounterTarget`, no
 * stream is added to `RNG_STREAM_NAMES`, `SNAPSHOT_VERSION` is untouched, and
 * not one draw is taken.** Every stage below is a pure function of two values
 * the target record has carried since long before there was a ship:
 *
 * | what | read from | who already wrote it |
 * |---|---|---|
 * | how badly she is hurt | `integrity` | `targets.ts`'s `wound` |
 * | how long she has been going | `burnSeconds` | `targets.ts`'s `burn` |
 *
 * So the whole feature rides `damagePerHit` and `bombVulnerability` becoming
 * non-zero in `target-profiles.ts`, exactly as that file's own note promised it
 * would — *"when the follow-up lane builds the damage state, those two zeros
 * are the whole of what has to change"*.
 *
 * **There is no damage roll.** The brief anticipated one and asked which stream
 * it would draw from; the answer is that this engine does not roll for target
 * damage at all. `encounter-tick.ts`'s own pass-order header says it: *"Nothing
 * here is a probability — hits are the geometry and only the geometry."* A
 * round either intersects her sphere or it does not, and what it takes off is
 * `damagePerHit × 1`. The Maxims she fires *back* still draw on `airship`, on
 * §4.3's terms and at §4.3's stated cost; the damage she *takes* draws on
 * nothing.
 *
 * ## The sinking pose is closed form, like the patrol it leaves
 *
 * `ship-patrol.ts`'s whole argument is that a pose is a pure function of the
 * clock, so there is no accumulator and two lockstep peers agree without
 * exchanging anything. Sinking keeps that. She was struck at
 * `timeS − burnSeconds`; her pose then is `patrolPoseAt` at that instant, which
 * the patrol will answer for any argument; and everything after it — the way
 * she carries, the list, the trim, the depth — is an elementary function of
 * `burnSeconds` alone. `shipMotionAt` is therefore callable for **any** past
 * instant, which is what lets the wake and the funnel plume bend correctly
 * through the moment she was hit rather than starting again from it.
 */

import { SHIP_LENGTH_OVERALL_M, SHIP_SPEED_MPS } from "./ship-class.ts";
import type { AmmunitionType } from "./target-kinds.ts";
import { patrolPoseAt, type ShipPatrol } from "./ship-patrol.ts";

const DEG = Math.PI / 180;

/**
 * Integrity a machine-gun hit takes off her, by belt. **OURS — a declared
 * invention with the argument written down, which is what the house asks for
 * when there is nothing to cite.**
 *
 * There is nothing to cite. No aeroplane sank a warship with rifle-calibre fire
 * in the Great War, and a model calibrated on that fact would give a figure of
 * zero — which is what shipped, and which the user has now asked to change.
 * So the number is chosen against **this port's own ladder** rather than
 * against history, and it is chosen to make one property measurable:
 *
 * > **One aeroplane cannot sink her.** At `regular` she takes **2 223** hits,
 * > and a two-gun scout in this port carries 1 000 rounds
 * > (`SHIP_MG_ROUNDS_PER_STATION` is the fleet's own `roundsPerGun`). More than
 * > twice a full load, every round of it on target, is beyond any single
 * > sortie — so `ship-sim.md` §5's sentence, *"two Vickers against 81 metres of
 * > riveted steel is not a threat to the ship"*, survives its own repeal.
 * > `ship-damage.test.ts` asserts it rather than this paragraph claiming it.
 *
 * The value itself is **half `target-profiles.ts`'s `MG_DAMAGE_PER_HIT`**, the
 * figure its three structures use. That is the smallest honest statement of
 * "tougher than the toughest thing this port already models against
 * rifle-calibre fire": a supply dump is timber and canvas and a warship's side
 * is riveted steel plate, so she must sit *below* the structure rung, and one
 * rung below is where the ladder puts her. CALIBRATION-PENDING, like every
 * other damage number in this port, and unlike most of them it can never be
 * anything else — there is no measurement of this that could exist.
 */
export const SHIP_DAMAGE_PER_HIT: Readonly<Record<AmmunitionType, number>> = {
  regular: 0.000_45,
  incendiary: 0.000_8,
};

/**
 * Bombs of the reference load, at the centre of the blast, to sink her.
 * **OURS.**
 *
 * Six, and it makes her the hardest thing in this port to bomb — the factory is
 * four and everything else is one or two. That ordering is the whole content of
 * the number: a bomb that levels a shed merely holes a warship, whose sides are
 * plated and whose hull is divided into compartments that a shed is not.
 *
 * Bombing her cost this lane nothing to enable. `encounter-tick.ts`'s
 * `settleTargets` already offers every bomb impact to every target through
 * `blastShare`, and `blastShare` already early-outs on `bombVulnerability <= 0`
 * — which is the single line that had been keeping her dry. So the raid path
 * reaches ships by deleting a zero, and this constant is what replaces it.
 */
export const SHIP_BOMBS_TO_SINK = 6;

/**
 * Integrity below which she is visibly smoking, and below which she is on fire.
 * **OURS**, and stated in rounds rather than in fractions because rounds are
 * what a pilot spends:
 *
 * - **Smoke at 0.60** — about **890** `regular` hits, which is one two-gun
 *   scout's entire belt landed on her. So a pilot who empties both guns into
 *   her leaves her making smoke *and no more than smoke*, which is the picture
 *   this whole calibration exists to produce: he can see he has hurt her, and
 *   he can see he is not going to finish her.
 * - **Fire at 0.25** — about **1 670** hits, beyond one aeroplane's ammunition
 *   and inside a flight's. A ship alight is a ship a flight has worked over.
 *
 * Below both of those, integrity reaching zero is what sets `burning` and
 * starts the sink clock. The three stages are therefore one ladder with one
 * quantity on it, and nothing has to be kept in step by hand.
 */
export const SHIP_SMOKE_INTEGRITY = 0.6;
export const SHIP_FIRE_INTEGRITY = 0.25;

/**
 * Seconds from the moment she is finished to the moment she is gone.
 * **OURS.**
 *
 * Sixty, and the argument is that a warship's death is the slowest one in this
 * port and should read as one. A gasbag burns for fourteen seconds, a factory
 * for twelve, a searchlight for half of one; if a 1 200-ton hull went the way
 * of a supply dump the picture would be a building collapsing on water. Real
 * sloops mined or torpedoed took minutes to half an hour, so sixty seconds is
 * already a heavy compression of the truth in the fast direction — it is short
 * enough that the pilot who did it sees it finish inside his sortie.
 *
 * One thing falls out of it that is worth pinning rather than admiring: it is
 * within a few seconds of `WAKE_LIFE_S`, the fifty-six seconds her foam stays
 * on the water. So she and the last of her own track go off the sea at about
 * the same moment, and there is no interval where a wake lies behind nothing.
 * `ship-damage.test.ts` measures the two against each other, because a comment
 * claiming two numbers are close is the thing this codebase has been burned by.
 */
export const SHIP_SINK_SECONDS = 60;

/**
 * Seconds her way takes to come off her. **Derived, not chosen**: it is set so
 * that her run-on is **exactly one ship length**.
 *
 * With her engines gone and her helm unattended she coasts, and her speed falls
 * from `SHIP_SPEED_MPS` to nothing over this interval. A linear fall covers
 * `V · T / 2`, so `T = 2 · LOA / V` puts `V · T / 2 = LOA` — and one ship
 * length of run-on is the same standing seamanship figure `SHIP_TURN_EASE_M`
 * already cites for how far a ship carries on before answering her helm. The
 * one number the port has for "how far this hull goes before anything it does
 * takes effect", used twice.
 */
export const SHIP_SINK_WAY_SECONDS = (2 * SHIP_LENGTH_OVERALL_M) / SHIP_SPEED_MPS;

/**
 * The list she takes, radians. **OURS — 25°.**
 *
 * Far enough over that she is unmistakably finished and her lee deck edge is
 * nearly in the water; short enough that she has not capsized, which is a
 * different picture and would want geometry this model does not have. An
 * Arabis's righting arm is gone somewhere in the thirties, so twenty-five is a
 * ship on her way down rather than a ship rolling over.
 */
export const SHIP_SINK_LIST_RAD = 25 * DEG;

/**
 * The trim she takes, radians, **bow up — she goes by the stern. OURS — 12°.**
 *
 * Which end goes first is a modelling choice and this port states it rather
 * than leaving it to a sign: her boiler and engine spaces are amidships and
 * aft, so the after part of her is where the water is, and the bow lifting is
 * the picture every photograph of the period has in it. Twelve degrees is as
 * far as it goes before she slides; more would put her mainmast in the sea
 * while her forefoot was still six metres up, which at 81 metres reads as a toy
 * rather than as a ship.
 */
export const SHIP_SINK_TRIM_RAD = 12 * DEG;

/**
 * Metres she settles bodily before she goes — her own **amidships freeboard**,
 * `ship-design.md` §3.2's 2.60 m. At the end of the settling phase her weather
 * deck is exactly awash, which is not a number chosen for the look: it is the
 * definition of decks awash, taken off the hull.
 */
export const SHIP_SINK_SETTLE_M = 2.6;

/**
 * Metres down she is when she is struck off the roster. **OURS**, and bounded
 * from below by the model rather than picked: her foremast truck is the
 * highest point in the `.glb` at **16.50 m** (`ship-design.md` §3.2), so
 * anything less than that leaves a spar standing on the water at the instant
 * she is deleted, which is the pop this constant exists to prevent. Twenty
 * clears it with three and a half metres to spare.
 */
export const SHIP_SINK_DEPTH_M = 20;

/** End of the phase in which her way comes off and she takes her list, as a fraction of the sink clock. */
export const SHIP_SINK_LIST_PHASE = SHIP_SINK_WAY_SECONDS / SHIP_SINK_SECONDS;

/** End of the phase in which she settles to decks-awash and trims by the stern. */
export const SHIP_SINK_SETTLE_PHASE = 0.8;

/**
 * What a ship is doing, as far as anything that draws her is concerned.
 *
 * Derived every time it is asked rather than stored, for the reason in the
 * module note: a stage that were a field would be a field in the snapshot, and
 * this feature's whole claim is that it adds none.
 */
export const SHIP_STAGES = ["steaming", "smoking", "afire", "sinking", "gone"] as const;

export type ShipStage = (typeof SHIP_STAGES)[number];

/** The two fields of a hull's `EncounterTarget` this module reads, and nothing else. */
export interface ShipHullState {
  readonly integrity: number;
  readonly burning: boolean;
  readonly burnSeconds: number;
  readonly destroyed: boolean;
}

/** Her stage, off her hull. Ordered: each one is strictly worse than the last. */
export function shipStageOf(hull: ShipHullState): ShipStage {
  if (hull.destroyed) return "gone";
  if (hull.burning) return "sinking";
  if (hull.integrity <= SHIP_FIRE_INTEGRITY) return "afire";
  if (hull.integrity <= SHIP_SMOKE_INTEGRITY) return "smoking";
  return "steaming";
}

/**
 * Whether she is still a warship: her guns lay, her lights burn, her battery
 * stands on the flak list.
 *
 * The moment integrity runs out she is none of those things, and this is the
 * one predicate every consumer asks so that they cannot drift apart —
 * `ship-pass.ts` was already asking it in longhand (`!destroyed && !burning`)
 * before there was anything that could make it false.
 */
export function shipFightsOn(hull: ShipHullState): boolean {
  return !hull.destroyed && !hull.burning;
}

/**
 * How far past the smoking threshold she is, 0 to 1 — what the funnel plume
 * thickens and darkens on.
 *
 * Zero at `SHIP_SMOKE_INTEGRITY` and one at integrity zero, so it is continuous
 * across the fire threshold rather than stepping at it: a ship does not start
 * making twice the smoke on the round that lights her.
 */
export function shipDistress(hull: ShipHullState): number {
  if (!shipFightsOn(hull)) return 1;
  if (hull.integrity >= SHIP_SMOKE_INTEGRITY) return 0;
  return Math.min(1, Math.max(0, (SHIP_SMOKE_INTEGRITY - hull.integrity) / SHIP_SMOKE_INTEGRITY));
}

/**
 * Seconds into her sinking, clamped to the sequence's own length.
 *
 * `burnSeconds` is the clock `targets.ts` already runs for anything alight, and
 * `advanceTarget` stops advancing it the tick she is destroyed, so this needs
 * no upper guard of its own — the clamp is belt and braces for a caller that
 * hands in a hull it built by hand.
 */
export function shipSinkElapsedS(hull: ShipHullState): number {
  if (!hull.burning && !hull.destroyed) return 0;
  return Math.min(SHIP_SINK_SECONDS, Math.max(0, hull.burnSeconds));
}

/**
 * Where she is, which way she is pointing, how fast she is going and how far
 * over — everything a renderer or a pass needs about one ship at one instant.
 *
 * `x`, `z` and `headingRad` alone are `ship-wake.ts`'s `WakePose`, deliberately:
 * this record satisfies that interface structurally, so the wake takes it with
 * no adapter.
 */
export interface ShipMotion {
  readonly x: number;
  readonly z: number;
  /** Metres **below** her marks. Zero while she floats; `SHIP_SINK_DEPTH_M` at the end. */
  readonly sinkM: number;
  readonly headingRad: number;
  readonly speedMps: number;
  /** Radians of heel from flooding, to starboard. Nothing to do with the heel of a turn. */
  readonly listRad: number;
  /** Radians of trim, positive **bow up**. */
  readonly trimRad: number;
}

/** Her speed `elapsedS` into the sinking — linear from service speed to nothing over `SHIP_SINK_WAY_SECONDS`. */
export function shipSinkSpeedMps(elapsedS: number): number {
  if (elapsedS <= 0) return SHIP_SPEED_MPS;
  return SHIP_SPEED_MPS * Math.max(0, 1 - elapsedS / SHIP_SINK_WAY_SECONDS);
}

/**
 * How far she has run on past the point she was struck, metres — the integral
 * of `shipSinkSpeedMps`, in closed form because a linear ramp integrates to a
 * parabola and a parabola is not an accumulator.
 *
 * Saturates at exactly `SHIP_LENGTH_OVERALL_M`; see `SHIP_SINK_WAY_SECONDS`.
 */
export function shipRunOnM(elapsedS: number): number {
  if (elapsedS <= 0) return 0;
  if (elapsedS >= SHIP_SINK_WAY_SECONDS) return SHIP_LENGTH_OVERALL_M;
  return SHIP_SPEED_MPS * elapsedS * (1 - elapsedS / (2 * SHIP_SINK_WAY_SECONDS));
}

/** A linear ramp from 0 to 1 across `[from, to]`, clamped at both ends. */
function ramp(value: number, from: number, to: number): number {
  if (to <= from) return value >= to ? 1 : 0;
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}

/**
 * The three phases of going down, each linear inside its own share of the sink
 * clock, each boundary a stated picture rather than a tuned number.
 *
 * 1. `[0, SHIP_SINK_LIST_PHASE]` — **her way comes off and she takes her
 *    list.** She runs on one ship length and lies over 25°.
 * 2. `[…, SHIP_SINK_SETTLE_PHASE]` — **she settles and trims by the stern.**
 *    She goes down by her own freeboard, so her weather deck ends this phase
 *    exactly awash, and her bow comes up 12°.
 * 3. `[…, 1]` — **she goes**, from decks-awash to `SHIP_SINK_DEPTH_M`, which is
 *    past her own masthead. Twelve seconds at about a metre and a half a
 *    second.
 *
 * Piecewise linear rather than a smooth curve on purpose: every one of those
 * three sentences is then a thing a test can measure at a named instant, where
 * an eased polynomial would only be checkable at its endpoints.
 */
function sinkAttitude(elapsedS: number): { sinkM: number; listRad: number; trimRad: number } {
  const u = Math.min(1, Math.max(0, elapsedS / SHIP_SINK_SECONDS));
  const listing = ramp(u, 0, SHIP_SINK_LIST_PHASE);
  const settling = ramp(u, SHIP_SINK_LIST_PHASE, SHIP_SINK_SETTLE_PHASE);
  const going = ramp(u, SHIP_SINK_SETTLE_PHASE, 1);
  return {
    sinkM: SHIP_SINK_SETTLE_M * settling + (SHIP_SINK_DEPTH_M - SHIP_SINK_SETTLE_M) * going,
    listRad: SHIP_SINK_LIST_RAD * listing,
    trimRad: SHIP_SINK_TRIM_RAD * settling,
  };
}

/**
 * **One ship at one instant**, whether she is steaming her circuit or going
 * down on her last course.
 *
 * `sinkElapsedS <= 0` is the healthy case and gives exactly what
 * `patrolPoseAt` gave before this file existed. Above zero she was struck at
 * `timeS − sinkElapsedS`, and both arguments moving together is what makes a
 * *past* pose askable: her pose `ageS` ago is
 * `shipMotionAt(patrol, timeS − ageS, sinkElapsedS − ageS)`, whose difference —
 * the instant she was hit — is the same in every such call. That is why her
 * wake and her plume bend through the moment she was hit instead of restarting
 * at it.
 */
export function shipMotionAt(patrol: ShipPatrol, timeS: number, sinkElapsedS: number): ShipMotion {
  if (sinkElapsedS <= 0) {
    const point = patrolPoseAt(patrol, timeS, SHIP_SPEED_MPS);
    return {
      x: point.x,
      z: point.z,
      sinkM: 0,
      headingRad: point.headingRad,
      speedMps: SHIP_SPEED_MPS,
      listRad: 0,
      trimRad: 0,
    };
  }
  const elapsed = Math.min(SHIP_SINK_SECONDS, sinkElapsedS);
  // Where she was when the last of her integrity went. `patrolPoseAt` answers
  // for any argument, so this is a call and not a remembered position — which
  // is what keeps the whole sequence out of the snapshot.
  const struck = patrolPoseAt(patrol, timeS - sinkElapsedS, SHIP_SPEED_MPS);
  const run = shipRunOnM(elapsed);
  // Body forward is −Z (`math3d.ts`), and she holds the head she was on: there
  // is nobody at the wheel.
  const attitude = sinkAttitude(elapsed);
  return {
    x: struck.x + Math.sin(struck.headingRad) * run,
    z: struck.z - Math.cos(struck.headingRad) * run,
    sinkM: attitude.sinkM,
    headingRad: struck.headingRad,
    speedMps: shipSinkSpeedMps(elapsed),
    listRad: attitude.listRad,
    trimRad: attitude.trimRad,
  };
}
